import { ChevronLeft, Edit2, RefreshCw, Check } from 'lucide-react';
import { useState } from 'react';
import { useApp } from '@/context/app-context';
import { useNavigate } from 'react-router-dom';
import { toDateStr } from '@/lib/helpers';
import { Logo } from '@/components/logo';

export function ReviewWorkout() {
  const { state } = useApp();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'workouts' | 'schedule'>('workouts');
  const [expandedWorkout, setExpandedWorkout] = useState<string | null>(null);

  const workoutsList = Object.values(state.workouts);
  const historyEntries = state.history;

  // Build a map of workoutId -> recent history entries
  const getWorkoutHistory = (workoutId: string) => {
    return historyEntries.filter((h) => h.workoutId === workoutId).slice(-5);
  };

  const getEffortColor = (rir?: number) => {
    if (rir === undefined) return '#71717A';
    if (rir === 0) return '#EF4444'; // Max
    if (rir === 1) return '#FF6B35'; // Hard
    if (rir === 2) return '#FFA033'; // Light
    return '#71717A'; // Warmup
  };

  const getEffortLabel = (rir?: number) => {
    if (rir === undefined) return 'Unknown';
    if (rir === 0) return 'Max';
    if (rir === 1) return 'Hard';
    if (rir === 2) return 'Light';
    return 'Warmup';
  };

  const getWorkoutTypeColor = (workout: { name: string }) => {
    const name = workout.name.toLowerCase();
    if (name.includes('push')) return '#FF6B35';
    if (name.includes('pull')) return '#FFA033';
    if (name.includes('leg')) return '#A855F7';
    return '#FF6B35';
  };

  const toggleExpand = (id: string) => {
    setExpandedWorkout(expandedWorkout === id ? null : id);
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#18181B', width: '390px', margin: '0 auto' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-4"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <button
          onClick={() => navigate('/workouts')}
          style={{
            minWidth: '44px',
            minHeight: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          <ChevronLeft size={24} style={{ color: '#F0F0F0' }} />
        </button>

        <div style={{ color: '#F0F0F0', fontSize: '16px', fontWeight: 700 }}>
          Review Your Program
        </div>

        <div style={{ width: '44px' }} /> {/* Spacer */}
      </div>

      {/* Tabs */}
      <div
        className="flex p-1 mx-4 mt-4 rounded-xl"
        style={{ backgroundColor: '#27272A', border: '1px solid rgba(255,255,255,0.06)' }}
      >
        <button
          onClick={() => setActiveTab('workouts')}
          className="flex-1 py-2.5 rounded-lg transition-all"
          style={{
            backgroundColor: activeTab === 'workouts' ? '#FF6B35' : 'transparent',
            color: activeTab === 'workouts' ? '#18181B' : '#71717A',
            fontSize: '14px',
            fontWeight: 700,
            cursor: 'pointer',
            border: 'none',
          }}
        >
          Workouts
        </button>
        <button
          onClick={() => setActiveTab('schedule')}
          className="flex-1 py-2.5 rounded-lg transition-all"
          style={{
            backgroundColor: activeTab === 'schedule' ? '#FF6B35' : 'transparent',
            color: activeTab === 'schedule' ? '#18181B' : '#71717A',
            fontSize: '14px',
            fontWeight: 700,
            cursor: 'pointer',
            border: 'none',
          }}
        >
          Schedule
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 pb-24">
        {/* Workouts Tab */}
        {activeTab === 'workouts' && (
          <div className="space-y-3">
            {workoutsList.length === 0 ? (
              <div
                className="p-6 text-center rounded-xl"
                style={{ backgroundColor: '#27272A', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <p style={{ color: '#71717A', fontSize: '14px' }}>No workouts created yet</p>
              </div>
            ) : (
              workoutsList.map((workout) => (
                <div
                  key={workout.id}
                  className="rounded-xl overflow-hidden"
                  style={{
                    backgroundColor: '#27272A',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  {/* Card Header */}
                  <button
                    onClick={() => toggleExpand(workout.id)}
                    className="w-full p-4 text-left"
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{
                              backgroundColor: getWorkoutTypeColor(workout),
                            }}
                          />
                          <span
                            style={{
                              color: '#71717A',
                              fontSize: '11px',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}
                          >
                            {workout.name}
                          </span>
                        </div>
                        <h3 style={{ color: '#F0F0F0', fontSize: '18px', fontWeight: 700, marginBottom: '8px' }}>
                          {workout.name}
                        </h3>
                        <div style={{ color: '#71717A', fontSize: '12px' }}>
                          {workout.exercises.length} exercises
                        </div>
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        className="rounded-lg flex items-center justify-center"
                        style={{
                          minWidth: '36px',
                          minHeight: '36px',
                          backgroundColor: '#3F3F46',
                          color: '#F0F0F0',
                          border: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        <Edit2 size={16} />
                      </button>
                    </div>

                    {/* Exercise Preview (when collapsed) */}
                    {expandedWorkout !== workout.id && (
                      <div className="mt-3 space-y-1">
                        {workout.exercises.slice(0, 3).map((exercise, i) => (
                          <div key={i} style={{ color: '#71717A', fontSize: '12px' }}>
                            • {exercise.name}
                          </div>
                        ))}
                        {workout.exercises.length > 3 && (
                          <div style={{ color: '#71717A', fontSize: '12px' }}>
                            + {workout.exercises.length - 3} more...
                          </div>
                        )}
                      </div>
                    )}
                  </button>

                  {/* Expanded Exercise List */}
                  {expandedWorkout === workout.id && (
                    <div
                      className="px-4 pb-4 pt-2 border-t"
                      style={{ borderColor: '#3F3F46' }}
                    >
                      <div className="space-y-2">
                        {workout.exercises.map((exercise, i) => (
                          <div
                            key={i}
                            className="p-3 rounded-lg"
                            style={{ backgroundColor: '#1F2228' }}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-start gap-2 flex-1">
                                <div
                                  className="flex items-center justify-center rounded"
                                  style={{
                                    minWidth: '24px',
                                    height: '24px',
                                    backgroundColor: '#3F3F46',
                                    color: '#A1A1AA',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                  }}
                                >
                                  {i + 1}
                                </div>
                                <div className="flex-1">
                                  <div
                                    style={{
                                      color: '#F0F0F0',
                                      fontSize: '14px',
                                      fontWeight: 600,
                                      marginBottom: '6px',
                                    }}
                                  >
                                    {exercise.name}
                                  </div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span style={{ color: '#71717A', fontSize: '12px' }}>
                                      {exercise.sets} sets × {exercise.reps}
                                    </span>
                                    {exercise.targetIntensity !== undefined && (
                                      <span
                                        className="px-2 py-0.5 rounded"
                                        style={{
                                          backgroundColor: getEffortColor(exercise.targetIntensity) + '20',
                                          color: getEffortColor(exercise.targetIntensity),
                                          fontSize: '11px',
                                          fontWeight: 700,
                                        }}
                                      >
                                        {getEffortLabel(exercise.targetIntensity)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Recent History for this Exercise */}
                            {getWorkoutHistory(workout.id).length > 0 && (
                              <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                                <div style={{ color: '#71717A', fontSize: '10px', marginBottom: '4px' }}>
                                  Recent:
                                </div>
                                {getWorkoutHistory(workout.id).map((history) => {
                                  const historyEx = history.exercises.find((ex) => ex.name === exercise.name);
                                  if (!historyEx) return null;
                                  return (
                                    <div key={history.date} style={{ color: '#A1A1AA', fontSize: '11px', marginBottom: '4px' }}>
                                      {history.date}: {historyEx.sets.map((s) => `${s.weight}×${s.reps}`).join(', ')}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Schedule Tab */}
        {activeTab === 'schedule' && (
          <div>
            {state.scheduleType === 'weekly' ? (
              <div
                className="p-4 rounded-xl mb-4"
                style={{ backgroundColor: '#27272A', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <h3 style={{ color: '#F0F0F0', fontSize: '14px', fontWeight: 700, marginBottom: '16px' }}>
                  Weekly Training Schedule
                </h3>

                <div className="space-y-2">
                  {state.weeklySchedule.map((slot) => {
                    const workout = slot.workoutId ? state.workouts[slot.workoutId] : null;
                    const color = workout ? getWorkoutTypeColor(workout) : '';
                    return (
                      <div
                        key={slot.day}
                        className="p-3 rounded-lg flex items-center gap-3"
                        style={{
                          backgroundColor: workout ? '#1F2228' : '#1A1A1D',
                          border: workout ? `1px solid ${color}20` : '1px solid rgba(255,255,255,0.03)',
                        }}
                      >
                        <div
                          className="flex items-center justify-center rounded-lg"
                          style={{
                            minWidth: '56px',
                            height: '56px',
                            backgroundColor: '#27272A',
                            border: '1px solid rgba(255,255,255,0.06)',
                          }}
                        >
                          <div className="text-center">
                            <div style={{ color: '#71717A', fontSize: '10px', fontWeight: 600, marginBottom: '2px' }}>
                              {slot.day.substring(0, 3)}
                            </div>
                            {workout && (
                              <div
                                className="w-2 h-2 rounded-full mx-auto"
                                style={{ backgroundColor: color }}
                              />
                            )}
                          </div>
                        </div>

                        {workout ? (
                          <div className="flex-1">
                            <div style={{ color: '#F0F0F0', fontSize: '14px', fontWeight: 600, marginBottom: '2px' }}>
                              {workout.name}
                            </div>
                            <div style={{ color: '#71717A', fontSize: '12px' }}>
                              {workout.exercises.length} exercises • ~{45 + workout.exercises.length * 5} min
                            </div>
                          </div>
                        ) : (
                          <div style={{ color: '#71717A', fontSize: '14px', fontWeight: 600 }}>
                            Rest Day
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div
                className="p-6 text-center rounded-xl"
                style={{ backgroundColor: '#27272A', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <p style={{ color: '#71717A', fontSize: '14px' }}>Using Cadence Schedule</p>
                <p style={{ color: '#A1A1AA', fontSize: '12px', marginTop: '8px' }}>
                  Rotation-based training
                </p>
              </div>
            )}

            {/* Legend */}
            {state.scheduleType === 'weekly' && (
              <div className="flex items-center gap-4 px-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#FF6B35' }} />
                  <span style={{ color: '#71717A', fontSize: '12px', fontWeight: 600 }}>Push</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#FFA033' }} />
                  <span style={{ color: '#71717A', fontSize: '12px', fontWeight: 600 }}>Pull</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#A855F7' }} />
                  <span style={{ color: '#71717A', fontSize: '12px', fontWeight: 600 }}>Legs</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Sticky Bar */}
      <div
        className="p-4"
        style={{
          backgroundColor: '#27272A',
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div className="flex items-center gap-2">
          <button
            className="flex-1 rounded-xl flex items-center justify-center gap-2"
            style={{
              backgroundColor: 'transparent',
              border: '2px solid #3F3F46',
              color: '#F0F0F0',
              padding: '14px',
              fontSize: '14px',
              fontWeight: 700,
              minHeight: '52px',
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={18} />
            Regenerate
          </button>

          <button
            className="flex-1 rounded-xl flex items-center justify-center gap-2"
            style={{
              backgroundColor: 'transparent',
              border: '2px solid #3F3F46',
              color: '#F0F0F0',
              padding: '14px',
              fontSize: '14px',
              fontWeight: 700,
              minHeight: '52px',
              cursor: 'pointer',
            }}
          >
            <Edit2 size={18} />
            Edit
          </button>

          <button
            onClick={() => navigate('/workouts')}
            className="flex-1 rounded-xl flex items-center justify-center gap-2"
            style={{
              backgroundColor: '#FF6B35',
              color: '#18181B',
              padding: '14px',
              fontSize: '14px',
              fontWeight: 700,
              minHeight: '52px',
              cursor: 'pointer',
              border: 'none',
            }}
          >
            <Check size={18} />
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}
